import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Gavel, CheckCircle2, CalendarCheck, Crown, Paperclip } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import DataTable from './DataTable';
import Badge from './common/Badge';
import Button from './common/Button';
import { useAppContext } from '../context/AppContext';
import { formatDate, filesToAttachments, formatFileSize } from '../utils/helpers';
import EmptyState from './common/EmptyState';
import { t } from '../i18n';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const statusConfig = {
  pending_id: { label: 'Pendiente ID', tone: 'warning' },
  in_evaluation: { label: 'En evaluación', tone: 'warning' },
  awarded: { label: 'Adjudicada', tone: 'success' },
  converted_to_order: { label: 'OC generada', tone: 'success' },
  rechazada: { label: 'Rechazada', tone: 'danger' },
  default: { label: 'Sin estado', tone: 'neutral' }
};

const formatAmount = (amount, currency = 'CLP') => {
  if (amount == null) return '-';
  const numericValue = Number(amount);
  if (Number.isNaN(numericValue)) return '-';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0
    }).format(numericValue);
  } catch (error) {
    return `${numericValue.toLocaleString('es-CL')} ${currency}`;
  }
};

const initialTenderForm = {
  title: '',
  scope: '',
  closingDate: '',
  estimatedValue: '',
  currency: 'CLP',
  projectId: '',
  relatedRequisitionId: '',
  attachments: []
};

const initialOfferForm = {
  supplierId: '',
  customSupplier: '',
  amount: '',
  currency: 'CLP',
  deliveryTime: '',
  notes: ''
};

const Tenders = () => {
  const {
    tenders,
    createTender,
    addTenderOffer,
    assignTenderId,
    awardTender,
    addTenderAttachments,
    updateTenderApproval,
    suppliers,
    parks,
    purchaseRequisitions,
    currentUser,
    findUserById
  } = useAppContext();

  if (!parks || parks.length === 0) {
    return (
      <EmptyState
        title={t('Licitaciones: faltan proyectos')}
        description={t(
          'Para levantar una RFQ necesitas un proyecto para asociar el requerimiento y su presupuesto.'
        )}
        checklist={['Crear proyecto en Desarrollo o Administración.']}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
        secondaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
      />
    );
  }

  if (!purchaseRequisitions || purchaseRequisitions.length === 0) {
    return (
      <EmptyState
        title={t('Licitaciones: no hay PR para licitar')}
        description={t(
          'El flujo recomendado parte con PR. Sin PR, la RFQ queda sin trazabilidad de imputación y aprobación.'
        )}
        checklist={['Crear PR en Abastecimiento > PR y enviarla a compras.']}
        primaryAction={{ label: 'Ir a PR', pageId: 'procurement-requisitions' }}
      />
    );
  }

  if (!suppliers || suppliers.length === 0) {
    return (
      <EmptyState
        title={t('Licitaciones: faltan proveedores')}
        description={t(
          'Para recibir ofertas necesitas proveedores creados (idealmente homologados).'
        )}
        checklist={['Crear proveedores en Proveedores & Compliance o cargar templates.']}
        primaryAction={{ label: 'Ir a Proveedores', pageId: 'suppliers-overview' }}
        secondaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
      />
    );
  }

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [formState, setFormState] = useState(initialTenderForm);
  const [formError, setFormError] = useState('');
  const [offerForm, setOfferForm] = useState(initialOfferForm);
  const [offerLineItems, setOfferLineItems] = useState([]);
  const [offerError, setOfferError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [detailUploadError, setDetailUploadError] = useState('');
  const [approvalNote, setApprovalNote] = useState('');

  const canCreateTender = useMemo(
    () => ['comprador', 'purchasing_manager', 'super_user'].includes(currentUser.role),
    [currentUser.role]
  );
  const canAddOffer = useMemo(
    () => ['comprador', 'purchasing_manager', 'super_user'].includes(currentUser.role),
    [currentUser.role]
  );
  const canAssignId = useMemo(
    () => ['purchase_controller', 'super_user'].includes(currentUser.role),
    [currentUser.role]
  );
  const canAward = useMemo(
    () => ['purchasing_manager', 'super_user'].includes(currentUser.role),
    [currentUser.role]
  );
  const canManageAttachments = useMemo(
    () =>
      ['comprador', 'purchasing_manager', 'purchase_controller', 'super_user'].includes(
        currentUser.role
      ),
    [currentUser.role]
  );

  const openRequisitions = useMemo(() => {
    const allowed = new Set(['pendiente_compra']);
    return (purchaseRequisitions || [])
      .filter((req) => {
        if (!allowed.has(req.status)) return false;
        if (req.linkedOrderId || req.tenderId) return false;
        if (currentUser.role === 'comprador' && req.routedToBuyerId && req.routedToBuyerId !== currentUser.id) {
          return false;
        }
        return true;
      })
      .sort((a, b) => (a.createdDate < b.createdDate ? 1 : -1));
  }, [purchaseRequisitions, currentUser]);

  const filteredRequisitions = useMemo(() => {
    if (!formState.projectId) return openRequisitions;
    return openRequisitions.filter((req) => req.parkId === formState.projectId);
  }, [formState.projectId, openRequisitions]);

  const projectOptions = useMemo(() => {
    return (parks || []).map((park) => ({ id: park.id, name: park.name }));
  }, [parks]);

  const approvalStatusTones = {
    aprobada: 'success',
    rechazada: 'danger',
    pendiente: 'warning'
  };

  const getApprovalLabel = (status = 'pendiente') => {
    if (status === 'aprobada') return 'Aprobada';
    if (status === 'rechazada') return 'Rechazada';
    return 'Pendiente';
  };

  const tableData = useMemo(() => {
    return tenders.map((tender) => {
      const offers = tender.offers || [];
      const bestOffer = offers.reduce((best, offer) => {
        if (offer.amount == null) return best;
        if (!best || Number(offer.amount) < Number(best.amount)) {
          return offer;
        }
        return best;
      }, null);
      return {
        ...tender,
        offersCount: offers.length,
        bestOffer
      };
    });
  }, [tenders]);

  const columns = [
    {
      key: 'tenderNumber',
      label: t('Folio'),
      sortable: true,
      render: (value) => value || 'Pendiente'
    },
    {
      key: 'title',
      label: t('Título'),
      sortable: true
    },
    {
      key: 'status',
      label: t('Estado'),
      sortable: true,
      render: (status) => {
        const config = statusConfig[status] || statusConfig.default;
        return <Badge tone={config.tone}>{t(config.label)}</Badge>;
      }
    },
    {
      key: 'closingDate',
      label: t('Cierre'),
      sortable: true,
      render: (value) => (value ? formatDate(value) : 'Sin definir')
    },
    {
      key: 'offersCount',
      label: t('Ofertas'),
      sortable: true
    },
    {
      key: 'bestOffer',
      label: t('Mejor oferta'),
      sortable: false,
      render: (offer, row) => {
        if (!offer) return 'Sin ofertas';
        return `${formatAmount(offer.amount, offer.currency || row.currency)} · ${offer.supplierName}`;
      }
    }
  ];

  useEffect(() => {
    if (!selectedTender) return;
    const refreshed = tenders.find((tender) => tender.id === selectedTender.id);
    if (!refreshed) {
      setSelectedTender(null);
    } else if (refreshed !== selectedTender) {
      setSelectedTender(refreshed);
    }
  }, [tenders, selectedTender]);

  const buildOfferLineItems = useCallback((requisition) => {
    if (!requisition?.lines?.length) return [];
    return requisition.lines.map((line, index) => ({
      id: line.id || `${requisition.id}-L${index + 1}`,
      sku: line.sku || '',
      description: line.description || '',
      quantity: Number(line.quantity || 0),
      unit: line.unit || 'unidad',
      unitPrice: Number(line.unitPrice || 0)
    }));
  }, []);

  const resetOfferForm = useCallback(() => {
    setOfferForm((prev) => ({
      ...initialOfferForm,
      currency: prev.currency || initialOfferForm.currency
    }));
    setOfferLineItems(buildOfferLineItems(selectedRequisition));
    setOfferError('');
  }, [buildOfferLineItems, selectedRequisition]);

  useEffect(() => {
    if (!selectedRequisition) {
      setOfferLineItems([]);
      return;
    }
    setOfferLineItems(buildOfferLineItems(selectedRequisition));
  }, [buildOfferLineItems, selectedRequisition]);

  const handleCreateTender = (event) => {
    event.preventDefault();
    if (!formState.title.trim()) {
      setFormError(t('Debes indicar un título para la licitación.'));
      return;
    }
    if (!formState.scope.trim()) {
      setFormError(t('Describe brevemente el alcance de la licitación.'));
      return;
    }
    if (!formState.relatedRequisitionId) {
      setFormError(t('Debes seleccionar una requisición abierta para vincular la licitación.'));
      return;
    }

    const linkedRequisition = purchaseRequisitions.find(
      (req) => req.id === formState.relatedRequisitionId
    );

    const tender = createTender({
      title: formState.title.trim(),
      scope: formState.scope.trim(),
      closingDate: formState.closingDate || null,
      estimatedValue: Number(formState.estimatedValue || 0),
      currency: formState.currency || 'CLP',
      relatedRequisitionId: formState.relatedRequisitionId || null,
      projectId: formState.projectId || linkedRequisition?.parkId || null,
      attachments: formState.attachments
    });

    setFormState(initialTenderForm);
    setFormError('');
    setShowCreateModal(false);
    setSelectedTender(tender);
    setInfoMessage(t('Licitación creada. Pendiente asignación de folio por Controller.'));
  };

  const handleCreateAttachmentUpload = async (event) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;
    setUploadError('');
    try {
      const uploads = await filesToAttachments(files);
      if (uploads.length === 0) return;
      setFormState((prev) => ({
        ...prev,
        attachments: [...uploads, ...prev.attachments]
      }));
    } catch (error) {
      console.error(error);
      setUploadError(t('No fue posible adjuntar los archivos. Inténtalo nuevamente.'));
    } finally {
      event.target.value = '';
    }
  };

  const removeCreateAttachment = (attachmentId) => {
    setFormState((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((attachment) => attachment.id !== attachmentId)
    }));
  };

  const hasLineItems = Boolean(selectedRequisition?.lines?.length);
  const offerItemsTotal = useMemo(
    () =>
      offerLineItems.reduce(
        (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0),
        0
      ),
    [offerLineItems]
  );

  const handleAddOffer = (event) => {
    event.preventDefault();
    if (!selectedTender) return;
    if (hasLineItems && offerItemsTotal <= 0) {
      setOfferError(t('Ingresa el valor unitario por ítem para calcular el total.'));
      return;
    }
    if (!hasLineItems && (!offerForm.amount || Number(offerForm.amount) <= 0)) {
      setOfferError(t('Ingresa el valor total de la oferta.'));
      return;
    }

    let supplierName = '';
    if (offerForm.supplierId === 'custom') {
      supplierName = offerForm.customSupplier.trim();
      if (!supplierName) {
        setOfferError(t('Indica el nombre del proveedor invitado.'));
        return;
      }
    } else {
      const supplier = suppliers.find((item) => item.id === offerForm.supplierId);
      if (!supplier) {
        setOfferError(t('Selecciona un proveedor de la lista o ingresa uno manualmente.'));
        return;
      }
      supplierName = supplier.name;
    }

    addTenderOffer(selectedTender.id, {
      supplierId: offerForm.supplierId === 'custom' ? null : offerForm.supplierId,
      supplierName,
      amount: hasLineItems ? Number(offerItemsTotal) : Number(offerForm.amount),
      currency: offerForm.currency || selectedTender.currency || 'CLP',
      deliveryTime: Number(offerForm.deliveryTime || 0),
      notes: offerForm.notes || '',
      lineItems: hasLineItems
        ? offerLineItems.map((item) => ({
            ...item,
            total: Number(item.quantity || 0) * Number(item.unitPrice || 0)
          }))
        : []
    });

    resetOfferForm();
    setInfoMessage(t('Oferta registrada correctamente.'));
  };

  const handleOfferLineChange = (index, value) => {
    setOfferLineItems((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, unitPrice: Number(value) }
          : item
      )
    );
  };

  const handleAssignFolio = () => {
    if (!selectedTender) return;
    const code = assignTenderId(selectedTender.id);
    if (code) {
      setInfoMessage(t('Se asignó el folio {0} a la licitación.', code));
    }
  };

  const handleAwardTender = (offerId) => {
    if (!selectedTender) return;
    awardTender(selectedTender.id, offerId);
    setInfoMessage(t('Licitación adjudicada correctamente.'));
  };

  const bestOffer = useMemo(() => {
    if (!selectedTender || !selectedTender.offers) return null;
    return selectedTender.offers.reduce((best, offer) => {
      if (offer.amount == null) return best;
      if (!best || Number(offer.amount) < Number(best.amount)) {
        return offer;
      }
      return best;
    }, null);
  }, [selectedTender]);

  const awardedOffer = useMemo(() => {
    if (!selectedTender?.awardedOfferId) return null;
    return selectedTender.offers?.find((offer) => offer.id === selectedTender.awardedOfferId) || null;
  }, [selectedTender]);

  const chartData = useMemo(() => {
    if (!selectedTender || !selectedTender.offers || selectedTender.offers.length === 0) {
      return null;
    }
    return {
      labels: selectedTender.offers.map((offer) => offer.supplierName || 'Proveedor'),
      datasets: [
        {
          label: t('Valor ofertado'),
          data: selectedTender.offers.map((offer) => Number(offer.amount || 0)),
          backgroundColor: selectedTender.offers.map((offer) => {
            if (selectedTender.awardedOfferId === offer.id) return 'rgba(31, 157, 85, 0.75)';
            if (bestOffer && bestOffer.id === offer.id) return 'rgba(47, 111, 184, 0.75)';
            return 'rgba(154, 164, 175, 0.6)';
          })
        }
      ]
    };
  }, [selectedTender, bestOffer]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${formatAmount(context.parsed.y, selectedTender?.currency || 'CLP')}`
          }
        }
      },
      scales: {
        y: {
          ticks: {
            callback: (value) => formatAmount(value, selectedTender?.currency || 'CLP')
          }
        }
      }
    }),
    [selectedTender]
  );

  const selectedRequisition = useMemo(() => {
    if (!selectedTender?.relatedRequisitionId) return null;
    return purchaseRequisitions.find((req) => req.id === selectedTender.relatedRequisitionId) || null;
  }, [purchaseRequisitions, selectedTender]);

  const projectApproval = selectedTender?.approvals?.projectManager;
  const purchasingApproval = selectedTender?.approvals?.purchasingManager;
  const isProjectManagerApprover = Boolean(
    selectedTender &&
      (currentUser.role === 'super_user' || projectApproval?.userId === currentUser.id)
  );
  const isPurchasingManagerApprover = Boolean(
    selectedTender &&
      (currentUser.role === 'super_user' || purchasingApproval?.userId === currentUser.id)
  );

  const handleRowClick = (row) => {
    const tender = tenders.find((item) => item.id === row.id);
    setSelectedTender(tender || null);
    resetOfferForm();
    setInfoMessage('');
    setDetailUploadError('');
    setApprovalNote('');
  };

  const handleDetailAttachmentUpload = async (event) => {
    if (!selectedTender) return;
    const { files } = event.target;
    if (!files || files.length === 0) return;
    setDetailUploadError('');
    try {
      const uploads = await filesToAttachments(files);
      if (uploads.length === 0) return;
      addTenderAttachments(selectedTender.id, uploads);
      setInfoMessage(t('Adjuntos agregados a la comparativa.'));
    } catch (error) {
      console.error(error);
      setDetailUploadError(t('No fue posible adjuntar los archivos. Inténtalo nuevamente.'));
    } finally {
      event.target.value = '';
    }
  };

  const handleApprovalDecision = (stage, decision) => {
    if (!selectedTender) return;
    updateTenderApproval(selectedTender.id, stage, decision, approvalNote.trim());
    setApprovalNote('');
    setInfoMessage(
      decision ? t('Aprobación registrada correctamente.') : t('La comparativa fue rechazada.')
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Licitaciones')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Compara ofertas, gestiona folios asignados por el Controller y adjudica a los mejores proveedores.'
          )}</p>
        </div>
        <div className="flex items-center gap-3">
          {canAssignId && selectedTender && !selectedTender.tenderNumber && (
            <Button variant="secondary" icon={Gavel} onClick={handleAssignFolio}>{t('Asignar folio')}</Button>
          )}
          {canCreateTender && (
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => {
                setFormState(initialTenderForm);
                setFormError('');
                setShowCreateModal(true);
              }}
            >{t('Nueva Licitación')}</Button>
          )}
        </div>
      </div>

      <DataTable
        data={tableData}
        columns={columns}
        searchPlaceholder={t('Buscar por folio, título o proveedor...')}
        onRowClick={handleRowClick}
      />

      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-6">
          <form
            className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8 space-y-6"
            onSubmit={handleCreateTender}
          >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{t('Nueva licitación')}</h3>
                  <p className="text-sm text-slate-500">{t('Registra un proceso de licitación para comparar ofertas y adjudicar.')}</p>
                </div>
                <button
                  type="button"
                  className="text-sm text-slate-500 hover:text-slate-900"
                  onClick={() => setShowCreateModal(false)}
                >{t('cerrar')}</button>
              </div>

              {formError && (
                <div className="rounded-xl border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-600">
                  {t(formError)}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Título')}</label>
                  <input
                    value={formState.title}
                    onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    placeholder={t('Repuestos, servicios, equipos...')}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Fecha de cierre')}</label>
                  <input
                    type="date"
                    value={formState.closingDate}
                    onChange={(event) => setFormState((prev) => ({ ...prev, closingDate: event.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Valor estimado')}</label>
                  <input
                    type="number"
                    min="0"
                    value={formState.estimatedValue}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, estimatedValue: event.target.value }))
                    }
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Proyecto')}</label>
                  <select
                    value={formState.projectId}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        projectId: event.target.value,
                        relatedRequisitionId: ''
                      }))
                    }
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                  >
                    <option value="">{t('Selecciona proyecto...')}</option>
                    {projectOptions.map((project) => (
                      <option key={project.id} value={project.id}>
                        {t(project.name)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-600">{t('Requisición vinculada')}</label>
                  <select
                    value={formState.relatedRequisitionId}
                    onChange={(event) => {
                      const nextId = event.target.value;
                      const requisition = openRequisitions.find((req) => req.id === nextId);
                      setFormState((prev) => ({
                        ...prev,
                        relatedRequisitionId: nextId,
                        projectId: requisition?.parkId || prev.projectId
                      }));
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                    required
                  >
                    <option value="">{t('Selecciona requisición abierta...')}</option>
                    {filteredRequisitions.map((req) => (
                      <option key={req.id} value={req.id}>
                        {t(req.id)} · {t(parks.find((park) => park.id === req.parkId)?.name) || t('Proyecto sin asignar')}
                      </option>
                    ))}
                  </select>
                  {filteredRequisitions.length === 0 && (
                    <p className="text-xs text-slate-400">{t('No hay requisiciones abiertas para este proyecto.')}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-600">{t('Alcance / alcance técnico')}</label>
                <textarea
                  value={formState.scope}
                  onChange={(event) => setFormState((prev) => ({ ...prev, scope: event.target.value }))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/30 focus:border-brand-400"
                  rows={4}
                  placeholder={t('Descripción breve del alcance, entregables, hitos relevantes...')}
                  required
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-600">{t('Adjuntar antecedentes (PDF, imágenes, planillas)')}</label>
                <input
                  type="file"
                  accept=".pdf,image/*,.xlsx,.xls,.csv"
                  multiple
                  onChange={handleCreateAttachmentUpload}
                  className="w-full px-4 py-3 border border-dashed border-neutral-300 rounded-xl text-sm text-slate-500 bg-neutral-50 hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
                />
                {uploadError && <p className="text-xs text-danger-600">{t(uploadError)}</p>}
                {formState.attachments.length > 0 && (
                  <div className="space-y-2">
                    {formState.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs text-slate-600"
                      >
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                          <div>
                            <p className="font-semibold text-slate-800">{t(attachment.name)}</p>
                            <p className="text-[11px] text-slate-400">
                              {formatFileSize(attachment.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCreateAttachment(attachment.id)}
                          className="text-danger-600 font-semibold hover:text-danger-700"
                        >{t('Quitar')}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button variant="secondary" onClick={() => setShowCreateModal(false)}>{t('Cancelar')}</Button>
                <Button variant="primary" type="submit">{t('Crear licitación')}</Button>
              </div>
            </form>
        </div>
      )}

      {selectedTender && (
        <div className="fixed inset-0 bg-slate-900/50 flex justify-end z-40">
            <div className="w-full max-w-3xl h-full bg-white border-l border-slate-100 shadow-2xl p-8 overflow-y-auto space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase">{t('Licitación')}</p>
                  <h3 className="text-xl font-bold text-slate-900">
                    {t(selectedTender.title)}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs font-semibold text-slate-500">{t('Folio:')}</span>
                    <span className="text-sm font-semibold text-slate-700">
                      {t(selectedTender.tenderNumber) || t('Pendiente asignación')}
                    </span>
                    <Badge tone={(statusConfig[selectedTender.status] || statusConfig.default).tone}>
                      {t((statusConfig[selectedTender.status] || statusConfig.default).label)}
                    </Badge>
                  </div>
                </div>
                <button
                  className="text-sm text-slate-500 hover:text-slate-900"
                  onClick={() => {
                    setSelectedTender(null);
                    setInfoMessage('');
                  }}
                >{t('cerrar')}</button>
              </div>

              {infoMessage && (
                <div className="rounded-xl border border-success-100 bg-success-50 px-4 py-3 text-sm text-success-700 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5" />
                  <p>{t(infoMessage)}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Fecha cierre')}</p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-slate-700">
                    <CalendarCheck className="w-4 h-4 text-slate-400" />
                    <span>{selectedTender.closingDate ? formatDate(selectedTender.closingDate) : t('Sin definir')}</span>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs text-slate-400 uppercase">{t('Monto estimado')}</p>
                  <p className="text-sm font-semibold text-slate-800 mt-2">
                    {formatAmount(selectedTender.estimatedValue, selectedTender.currency)}
                  </p>
                </div>
                {selectedRequisition && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 md:col-span-2">
                    <p className="text-xs text-slate-400 uppercase">{t('Requisición vinculada')}</p>
                    <p className="text-sm font-semibold text-slate-800 mt-2">
                      {t(selectedRequisition.id)} · {t(selectedRequisition.title)}
                    </p>
                    <p className="text-xs text-slate-400">{t('Proyecto:')} {t(parks.find((park) => park.id === selectedRequisition.parkId)?.name) || t('Sin proyecto')}
                    </p>
                    <p className="text-xs text-slate-400">{t('Solicitante:')} {t(selectedRequisition.requesterName) || t(selectedRequisition.requesterId)}
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-3">
                <p className="text-xs text-slate-400 uppercase">{t('Alcance')}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{t(selectedTender.scope) || t('Sin descripción')}</p>
              </div>

              <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400 uppercase">{t('Documentos adjuntos')}</p>
                  {canManageAttachments && (
                    <label className="text-xs font-semibold text-brand-600 cursor-pointer hover:text-brand-700">{t('Añadir archivos')}<input
                        type="file"
                        accept=".pdf,image/*,.xlsx,.xls,.csv"
                        multiple
                        onChange={handleDetailAttachmentUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                {detailUploadError && <p className="text-xs text-danger-600">{t(detailUploadError)}</p>}
                <div className="space-y-2">
                  {(selectedTender.attachments || []).length === 0 && (
                    <p className="text-xs text-slate-400">{t('Sin documentos adjuntos.')}</p>
                  )}
                  {(selectedTender.attachments || []).map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs text-slate-600"
                    >
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                        <div>
                          <p className="font-semibold text-slate-800">{t(attachment.name)}</p>
                          <p className="text-[11px] text-slate-400">
                            {formatFileSize(attachment.size)} ·{' '}
                            {attachment.uploadedAt ? formatDate(attachment.uploadedAt) : t('Sin fecha')}
                          </p>
                        </div>
                      </div>
                      {attachment.dataUrl ? (
                        <a
                          href={attachment.dataUrl}
                          download={attachment.name}
                          className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                        >{t('Descargar')}</a>
                      ) : (
                        <span className="text-xs text-slate-400">{t('Adjunto demo')}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-slate-400 uppercase">{t('Aprobaciones de comparativa')}</p>
                  {selectedTender.generatedOrderId && (
                    <span className="text-xs font-semibold text-success-600">{t('OC generada:')} {t(selectedTender.generatedOrderId)}
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t('Project Manager')}</p>
                      <p className="text-xs text-slate-400">
                        {projectApproval?.userId
                          ? t(findUserById(projectApproval.userId)?.name) || t('Asignación pendiente')
                          : t('Asignación pendiente')}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <Badge tone={approvalStatusTones[projectApproval?.status || 'pendiente']}>
                        {t(getApprovalLabel(projectApproval?.status))}
                      </Badge>
                      <p className="text-[11px] text-slate-400">
                        {projectApproval?.date ? formatDate(projectApproval.date) : t('Sin fecha')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t('Gerencia de Compras')}</p>
                      <p className="text-xs text-slate-400">
                        {purchasingApproval?.userId
                          ? t(findUserById(purchasingApproval.userId)?.name) || t('Asignación pendiente')
                          : t('Asignación pendiente')}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <Badge tone={approvalStatusTones[purchasingApproval?.status || 'pendiente']}>
                        {t(getApprovalLabel(purchasingApproval?.status))}
                      </Badge>
                      <p className="text-[11px] text-slate-400">
                        {purchasingApproval?.date ? formatDate(purchasingApproval.date) : t('Sin fecha')}
                      </p>
                    </div>
                  </div>
                </div>
                {(projectApproval?.status === 'pendiente' && isProjectManagerApprover) ||
                (purchasingApproval?.status === 'pendiente' && isPurchasingManagerApprover) ? (
                  <div className="space-y-3">
                    <textarea
                      value={approvalNote}
                      onChange={(event) => setApprovalNote(event.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/20"
                      rows={3}
                      placeholder={t('Comentario para la aprobación / rechazo')}
                    />
                    <div className="flex flex-wrap gap-2">
                      {isProjectManagerApprover && projectApproval?.status === 'pendiente' && (
                        <>
                          <Button
                            variant="primary"
                            icon={CheckCircle2}
                            onClick={() => handleApprovalDecision('project', true)}
                          >{t('Aprobar PM')}</Button>
                          <Button variant="danger" onClick={() => handleApprovalDecision('project', false)}>{t('Rechazar PM')}</Button>
                        </>
                      )}
                      {isPurchasingManagerApprover &&
                        purchasingApproval?.status === 'pendiente' && (
                          <>
                            <Button
                              variant="primary"
                              icon={CheckCircle2}
                              onClick={() => handleApprovalDecision('purchasing', true)}
                            >{t('Aprobar Compras')}</Button>
                            <Button
                              variant="danger"
                              onClick={() => handleApprovalDecision('purchasing', false)}
                            >{t('Rechazar Compras')}</Button>
                          </>
                        )}
                    </div>
                  </div>
                ) : null}
              </div>

              {chartData && (
                <div className="bg-white border border-slate-100 rounded-xl p-4">
                  <p className="text-xs text-slate-400 uppercase mb-3">{t('Comparativa de ofertas')}</p>
                  <div className="h-64">
                    <Bar data={chartData} options={chartOptions} />
                  </div>
                </div>
              )}

              <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">{t('Ofertas recibidas')}</p>
                  <span className="text-xs text-slate-400">
                    {t(selectedTender.offers?.length) || 0} {t('propuestas')}</span>
                </div>
                <div className="space-y-3">
                  {(selectedTender.offers || []).map((offer) => {
                    const isBest = bestOffer && bestOffer.id === offer.id;
                    const isAwarded = selectedTender.awardedOfferId === offer.id;
                    return (
                      <div
                        key={offer.id}
                        className={`rounded-2xl border p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
                          isAwarded
                            ? 'border-success-200 bg-success-50'
                            : isBest
                            ? 'border-info-200 bg-info-50'
                            : 'border-slate-100 bg-white'
                        }`}
                      >
                        <div>
                          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                            {t(offer.supplierName)}
                            {isAwarded && (
                              <Badge tone="success">
                                <Crown className="w-3 h-3" />{t('Adjudicado')}</Badge>
                            )}
                            {!isAwarded && isBest && <Badge tone="info">{t('Mejor precio')}</Badge>}
                          </p>
                          <p className="text-sm text-slate-600 mt-1">{t('Total:')} {formatAmount(offer.amount, offer.currency || selectedTender.currency)}·{' '}{t('Plazo:')}{' '}
                            {offer.deliveryTime
                              ? t('{0} días', offer.deliveryTime)
                              : t('No informado')}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">{t('Registrada:')} {offer.createdAt ? formatDate(offer.createdAt) : t('Sin fecha')} {t('· Puntaje')} {t(offer.score) || 0}
                          </p>
                          {offer.notes && (
                            <p className="text-xs text-slate-500 mt-1">{t(offer.notes)}</p>
                          )}
                          {offer.lineItems?.length > 0 && (
                            <div className="mt-3 border border-slate-200 rounded-xl overflow-hidden">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-100 text-slate-600 uppercase tracking-wide">
                                  <tr>
                                    <th className="px-3 py-2 text-left">{t('Material')}</th>
                                    <th className="px-3 py-2 text-left">{t('Cantidad')}</th>
                                    <th className="px-3 py-2 text-left">{t('Unitario')}</th>
                                    <th className="px-3 py-2 text-left">{t('Total')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {offer.lineItems.map((item) => (
                                    <tr key={item.id} className="border-t border-slate-200">
                                      <td className="px-3 py-2">
                                        <div className="text-slate-700 font-medium">{t(item.description)}</div>
                                        {item.sku && (
                                          <div className="text-[11px] text-slate-400">{t('SKU')} {t(item.sku)}</div>
                                        )}
                                      </td>
                                      <td className="px-3 py-2">{t(item.quantity)}</td>
                                      <td className="px-3 py-2">
                                        {formatAmount(item.unitPrice, offer.currency || selectedTender.currency)}
                                      </td>
                                      <td className="px-3 py-2 font-semibold text-slate-700">
                                        {formatAmount(item.total || (Number(item.quantity || 0) * Number(item.unitPrice || 0)), offer.currency || selectedTender.currency)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        {canAward &&
                          selectedTender.status !== 'awarded' &&
                          selectedTender.status !== 'converted_to_order' && (
                          <Button
                            variant="primary"
                            icon={Crown}
                            className="self-end md:self-auto"
                            onClick={() => handleAwardTender(offer.id)}
                          >{t('Adjudicar')}</Button>
                        )}
                      </div>
                    );
                  })}
                  {!selectedTender.offers?.length && (
                    <p className="text-sm text-slate-400">{t('Aún no se registran ofertas para esta licitación.')}</p>
                  )}
                </div>

                {canAddOffer && selectedTender.status !== 'awarded' && (
                  <form className="space-y-4 border-t border-slate-100 pt-4" onSubmit={handleAddOffer}>
                    <p className="text-xs text-slate-400 uppercase">{t('Registrar nueva oferta')}</p>
                    {offerError && (
                      <div className="rounded-xl border border-danger-100 bg-danger-50 px-3 py-2 text-xs text-danger-600">
                        {t(offerError)}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500">{t('Proveedor')}</label>
                        <select
                          value={offerForm.supplierId}
                          onChange={(event) =>
                            setOfferForm((prev) => ({ ...prev, supplierId: event.target.value }))
                          }
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/20"
                        >
                          <option value="">{t('Seleccionar proveedor...')}</option>
                          {suppliers.map((supplier) => (
                            <option key={supplier.id} value={supplier.id}>
                              {t(supplier.name)}
                            </option>
                          ))}
                          <option value="custom">{t('Otro proveedor...')}</option>
                        </select>
                      </div>
                      {offerForm.supplierId === 'custom' && (
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Nombre proveedor invitado')}</label>
                          <input
                            value={offerForm.customSupplier}
                            onChange={(event) =>
                              setOfferForm((prev) => ({ ...prev, customSupplier: event.target.value }))
                            }
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/20"
                            placeholder={t('Nombre comercial')}
                          />
                        </div>
                      )}
                      {hasLineItems ? (
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Detalle por material')}</label>
                          <div className="border border-slate-200 rounded-xl overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100 text-slate-600 uppercase tracking-wide">
                                <tr>
                                  <th className="px-3 py-2 text-left">{t('Material')}</th>
                                  <th className="px-3 py-2 text-left">{t('Cantidad')}</th>
                                  <th className="px-3 py-2 text-left">{t('Unidad')}</th>
                                  <th className="px-3 py-2 text-left">{t('Valor unitario')}</th>
                                  <th className="px-3 py-2 text-left">{t('Total')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {offerLineItems.map((item, index) => {
                                  const lineTotal = Number(item.quantity || 0) * Number(item.unitPrice || 0);
                                  return (
                                    <tr key={item.id} className="border-t border-slate-200">
                                      <td className="px-3 py-2">
                                        <div className="text-slate-700 font-medium">{t(item.description)}</div>
                                        {item.sku && (
                                          <div className="text-[11px] text-slate-400">{t('SKU')} {t(item.sku)}</div>
                                        )}
                                      </td>
                                      <td className="px-3 py-2">{t(item.quantity)}</td>
                                      <td className="px-3 py-2">{t(item.unit)}</td>
                                      <td className="px-3 py-2">
                                        <input
                                          type="number"
                                          min="0"
                                          value={item.unitPrice}
                                          onChange={(event) => handleOfferLineChange(index, event.target.value)}
                                          className="w-24 px-2 py-1 border border-slate-200 rounded-md"
                                          placeholder="0"
                                        />
                                      </td>
                                      <td className="px-3 py-2 font-semibold text-slate-700">
                                        {formatAmount(lineTotal, offerForm.currency || selectedTender.currency)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                            <span className="text-xs text-slate-500">{t('Total oferta')}</span>
                            <span className="text-sm font-semibold text-slate-800">
                              {formatAmount(offerItemsTotal, offerForm.currency || selectedTender.currency)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-500">{t('Monto ofertado')}</label>
                          <input
                            type="number"
                            min="0"
                            value={offerForm.amount}
                            onChange={(event) =>
                              setOfferForm((prev) => ({ ...prev, amount: event.target.value }))
                            }
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/20"
                            placeholder="0"
                            required
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500">{t('Plazo de entrega (días)')}</label>
                        <input
                          type="number"
                          min="0"
                          value={offerForm.deliveryTime}
                          onChange={(event) =>
                            setOfferForm((prev) => ({ ...prev, deliveryTime: event.target.value }))
                          }
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/20"
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <label className="text-xs font-semibold text-slate-500">{t('Notas')}</label>
                        <textarea
                          value={offerForm.notes}
                          onChange={(event) =>
                            setOfferForm((prev) => ({ ...prev, notes: event.target.value }))
                          }
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-400/20"
                          rows={2}
                          placeholder={t('Términos comerciales, consideraciones, garantías...')}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                      <Button variant="secondary" onClick={resetOfferForm}>{t('Limpiar')}</Button>
                      <Button variant="primary" type="submit">{t('Registrar oferta')}</Button>
                    </div>
                  </form>
                )}
              </div>

              <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase mb-2">{t('Historial')}</p>
                <div className="space-y-3">
                  {(selectedTender.history || []).map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between text-sm text-slate-600">
                      <div>
                        <p className="font-semibold text-slate-800">{t(entry.action)}</p>
                        {entry.note && <p className="text-xs text-slate-400">{t(entry.note)}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">
                          {entry.timestamp ? t(entry.timestamp) : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                  {!selectedTender.history?.length && (
                    <p className="text-xs text-slate-400">{t('Sin registros de actividad.')}</p>
                  )}
                </div>
              </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default Tenders;
